import React, {useCallback,useEffect,useState}from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faUsers, faClock, 
  faCheckCircle, faRobot, faBrain,
  faCloud, faCode, faProjectDiagram,
  faLaptopCode, faCertificate, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const AIDeveloper = () => {
    const [showModal, setShowModal] = useState(false);
      const [formCourseName, setFormCourseName] = useState('AI Developer');
      const [formData, setFormData] = useState({
        fullName: '',
        mobileNumber: '',
        email: '',
      });
    
      const openModal = () => setShowModal(true);
      const closeModal = useCallback(() => {
        setShowModal(false);
        setFormData({ fullName: '', mobileNumber: '', email: '' });
      }, []);
    
      useEffect(() => {
        const handleEsc = (e) => {
          if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
      }, [closeModal]);
    
      const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mobileNumber') {
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length <= 10) {
            setFormData(prev => ({ ...prev, [name]: digitsOnly }));
          }
        } else {
          setFormData(prev => ({ ...prev, [name]: value }));
        }
      };
    
      const handleSubmit = async (e) => {
        e.preventDefault();
        const { fullName, mobileNumber, email } = formData;
    
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
          alert('Please fill in valid details.');
          return;
        }
    
        try {
          const response = await fetch('https://api.dikshasea.in/api/enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, course: formCourseName }),
          });
    
          const result = await response.json();
          if (response.ok) {
            alert('Enrollment successful!');
            closeModal();
          } else {
            alert(result.message || 'Submission failed');
          }
        } catch (err) {
          console.error(err);
          alert('Error submitting form');
        }
      };
    
  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New AI Developer Program</span>
              <h1 className="hero-title">
                Become an <span className="hero-highlight">AI Developer</span>
              </h1>
              <p className="hero-subtitle">
                Learn to build intelligent systems using deep learning, machine learning, and neural networks. This program offers hands-on experience with AI models and deployment in production environments.
              </p>

              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.8/5.0</div>
                    <small className="text-muted">(2,800 reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">12,000+</div>
                    <small className="text-muted">Students</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">80 hours</div>
                    <small className="text-muted">Content</small>
                  </div>
                </div>
              </div>

             <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img
                  src="subcourses-images/ai-developers.jpg"
                  alt="AI Developer Program"
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            {/* About Course */}
            <section className="about-course">
              <h2 className="section-title">About This Course</h2>
              <p>This AI Developer course trains you to develop intelligent systems that can learn and adapt. Covering foundational and advanced topics, it prepares you to work with modern AI tools and frameworks to solve real-world problems.</p>
              <p>You will gain expertise in supervised/unsupervised learning, deep learning, computer vision, NLP, and model deployment on cloud platforms.</p>
            </section>

            {/* Curriculum */}
            <section className="curriculum">
              <h2 className="section-title">Curriculum</h2>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faBrain} className="me-2" /> Module 1: Foundations of AI</h3>
                <p>Understand the core principles of AI development</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Basics of AI and ML</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Python for AI</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Supervised and Unsupervised Learning</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Regression and Classification</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faRobot} className="me-2" /> Module 2: Deep Learning & Neural Networks</h3>
                <p>Gain practical experience building and training neural networks</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> TensorFlow & PyTorch</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> CNNs, RNNs, GANs</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Hyperparameter tuning</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model evaluation</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCloud} className="me-2" /> Module 3: AI in the Cloud</h3>
                <p>Deploy scalable AI applications using cloud platforms</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Google AI Platform</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> AWS SageMaker</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Azure ML Studio</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> MLOps Essentials</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCode} className="me-2" /> Module 4: NLP and Computer Vision</h3>
                <p>Work on exciting AI applications in real-world domains</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Transformers & BERT</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Object detection & image classification</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Text generation and summarization</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Voice assistants</div>
                </div>
              </div>

              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 5: Capstone AI Project</h3>
                <p>Build an intelligent AI product with real data</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> End-to-end ML pipeline</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Data preprocessing & feature engineering</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Model deployment</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Performance tracking</div>
                </div>
              </div>
            </section>
          </div>

          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>16 Weeks (15 hrs/week)</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Level</h4>
                <p>Intermediate to Advanced</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>7 Hands-on AI Projects</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>Python, Math (Linear Algebra, Probability)</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Certifications</h4>
                <p>AI Developer Certification</p>
              </div>
            </div>

            <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                  Enroll Now
                </button>
          </div>
        </div>
      </div>
      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">AI Developer</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-dark me-2" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Submit</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIDeveloper;
